import { spacing } from '@greasemap/design';

type SpaceKey = keyof typeof spacing;

/** Props for {@link Stack}. */
export interface StackProps {
  readonly children: React.ReactNode;
  /** Layout direction. Defaults to vertical. */
  readonly direction?: 'row' | 'column';
  /** Gap between children, from the spacing scale. */
  readonly gap?: SpaceKey;
  /** Cross-axis alignment. */
  readonly align?: React.CSSProperties['alignItems'];
  /** Main-axis distribution. */
  readonly justify?: React.CSSProperties['justifyContent'];
  /** Allow children to wrap onto a new line. */
  readonly wrap?: boolean;
}

/**
 * The layout primitive. Everything that needs spacing between elements uses this rather than
 * ad-hoc margins, which keeps vertical rhythm consistent and prevents margin collapsing surprises.
 *
 * @param props - Component props.
 * @returns The layout element.
 */
export function Stack({
  children,
  direction = 'column',
  gap = 'md',
  align,
  justify,
  wrap = false,
}: StackProps): React.JSX.Element {
  return (
    <div
      className="gm-stack"
      style={{
        flexDirection: direction,
        gap: `${spacing[gap]}px`,
        ...(align === undefined ? {} : { alignItems: align }),
        ...(justify === undefined ? {} : { justifyContent: justify }),
        ...(wrap ? { flexWrap: 'wrap' } : {}),
      }}
    >
      {children}
    </div>
  );
}
